import React from 'react';

import { i18n } from 'config/locales';

import ErrorMessageList from 'components/ErrorMessageList';
import { Summary, TelephoneSummary } from 'components/Summary';
import { IDENTIFICATION, IDENTIFICATION_CONTACTS } from 'config/formSections/identification';

import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection from 'components/Section/shared/Subsection';

import * as formTypes from 'constants/formTypes';
import { EmptyListInstance } from 'forms/shared/models';
import Field from 'components/Form/Field';
import Email from 'components/Form/Email';
import PVQEmail from 'components/Form/PVQEmail';
import Accordion, { AccordionItem } from 'components/Form/Accordion';
import Telephone from 'components/Form/Telephone';
import { isPVQForm } from 'helpers/form';

const sectionConfig = {
  key: IDENTIFICATION_CONTACTS.key,
  section: IDENTIFICATION.name,
  store: IDENTIFICATION.store,
  subsection: IDENTIFICATION_CONTACTS.name,
  storeKey: IDENTIFICATION_CONTACTS.storeKey,
};

/**
 * @param {Object} props
 * @param {boolean} props.scrollIntoView
 * @param {number} [props.$index] - provided by Accordion
 * @param {boolean} props.required
 * @param {boolean} props.bind - needed for Accordion.
 * @param {string} props.name - needed for Accordion
 *
 * There are other properties being added by the Accordion.
 *
 * This was reworked into a FunctionalComponent to receive $index and pass it
 * into Field for correct error mapping.
 */
const ContactInfoItem = function (props) {
  return (
    <Field
      optional
      scrollIntoView={props.scrollIntoView}
      attribute={`PhoneNumbers.items[${props.$index}]`}
    >
      <Telephone
        attribute={`PhoneNumbers.items[${props.$index}].Item.Telephone`}
        name="Telephone"
        typeClass="title"
        showNumberType
        allowNotApplicable={false}
        bind
        required={props.required}
        {...props}
      />
    </Field>
  );
};

export class ContactInformation extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.formType = props.formType;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      HomeEmail: this.props.HomeEmail,
      WorkEmail: this.props.WorkEmail,
      PhoneNumbers: this.props.PhoneNumbers,
      Emails: this.props.Emails,
      ...queue,
    });
  };

  updateHomeEmail = (values) => {
    this.update({
      HomeEmail: values,
    });
  };

  updateWorkEmail = (values) => {
    this.update({
      WorkEmail: values,
    });
  };

  updatePhoneNumbers = (values) => {
    this.update({
      PhoneNumbers: values,
    });
  };

  updateEmails = (values) => {
    this.update({
      Emails: values,
    });
  };

  /**
   * Assists in rendering the summary section.
   */
  phoneNumberSummary = (item, index) => {
    const number = TelephoneSummary((item.Item || {}).Telephone);
    return Summary({
      type: i18n.t('identification.contacts.collection.summary.phoneNumber'),
      index,
      left: number,
      right: null,
      placeholder: i18n.t('identification.contacts.collection.summary.unknownPhone'),
    });
  };

  emailSummary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const email = itemProperties.Email && itemProperties.Email.value ? itemProperties.Email.value : '';
    return Summary({
      type: i18n.t('identification.pvq.contacts.collection.summary.email'),
      index,
      left: email,
      right: null,
      placeholder: i18n.t('identification.pvq.contacts.collection.summary.unknownEmail'),
    });
  };

  firstItemRequired = (item, index, initial, callback) => {
    if (index === 0) {
      return <span className="required">{callback()}</span>;
    }

    return callback();
  };

  getSectionErrors = () => {
    const { errors = [] } = this.props;
    const sectionErrors = [];
    errors.forEach((error) => {
      if (error === 'PhoneNumbers.accordion.DUPLICATE_PHONE_NUMBER_TYPES') {
        sectionErrors.push({
          key: 'PhoneNumbers.accordion.DUPLICATE_PHONE_NUMBER_TYPES',
          title: i18n.t('error.validPhoneTypes.title'),
          message: i18n.t('error.validPhoneTypes.message'),
          shouldDisplayError: true,
        });
      }
    });

    return sectionErrors;
  };

  render() {
    const {
      HomeEmail = {}, WorkEmail = {}, isReview, errors, formType
    } = this.props;
    const klass = `${this.props.className || ''}`.trim();
    const isPVQ = isPVQForm(formType);

    let phoneNumbers = { ...this.props.PhoneNumbers };
    const accordionErrors = errors && errors.filter((e) => e.includes('accordion'));
    if (this.props.shouldFilterEmptyItems) {
      let filtered = phoneNumbers.items.length;
      const filteredPhoneNumbers = phoneNumbers.items.filter((x) => {
        const item = x.Item || {};
        if (!item.Telephone || !item.Telephone.value) {
          filtered -= 1;
          if (filtered < 1) {
            return item;
          }
        }
        return (item.Telephone && item.Telephone.number) || item.noNumber;
      });

      phoneNumbers = {
        ...phoneNumbers,
        items: filteredPhoneNumbers,
      };
    }

    let emails = { ...this.props.Emails };
    if (this.props.shouldFilterEmptyItems) {
      let filtered = emails.items.length;
      const filteredEmails = emails.items.filter((x) => {
        const item = x.Item || {};
        if (!item.Email || !item.Email.value) {
          filtered -= 1;
          if (filtered < 1) {
            return item;
          }
        }
        return (item.Email && item.Email.value);
      });

      emails = {
        ...emails,
        items: filteredEmails,
      };
    }

    let phoneMin = this.props.minimumPhoneNumbers;
    if (phoneNumbers.items.length === 0) {
      phoneMin = this.props.shouldFilterEmptyItems ? 1 : 2;
    }

    return isPVQ ? (
      <div
        className="section-content contact"
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_CONTACTS.key}
      >
        <h1 className="section-header">{i18n.t('identification.pvq.destination.contacts')}</h1>
        <label className="contacts-label">{i18n.m('identification.pvq.contacts.info')}</label>

        <Field
          title={i18n.t('identification.pvq.contacts.heading.phoneNumber')}
          titleSize="h3"
          optional
          help="identification.pvq.contacts.help.phoneNumber"
          className="no-margin-bottom"
          scrollIntoView={false}
        />

        <div className={`${klass} telephone-collection`}>
          <Accordion
            {...phoneNumbers}
            minimum={phoneMin}
            defaultState={this.props.defaultState}
            onUpdate={this.updatePhoneNumbers}
            onError={this.handleError}
            required={this.props.required}
            customDetails={this.firstItemRequired}
            errors={accordionErrors}
            summary={this.phoneNumberSummary}
            description={i18n.t('identification.pvq.contacts.collection.phoneNumbers.summary.title')}
            appendLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.append')}
            scrollIntoView={this.props.scrollIntoView}
            ignoreInitialScrollTo
          >
            <AccordionItem
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
              name="Item"
            >
              <PVQContactInfoItemTelephone
                scrollIntoView={this.props.scrollIntoView}
                bind
                name="Telephone"
                required={this.props.required}
              />
            </AccordionItem>
          </Accordion>
        </div>

        <Field
          title={i18n.t('identification.pvq.contacts.heading.email')}
          titleSize="h3"
          optional
          help="identification.pvq.contacts.help.email"
          className="no-margin-bottom"
          scrollIntoView={false}
        />

        <ErrorMessageList errors={this.getSectionErrors()} />

        <div className={`${klass} email-collection`}>
          <Accordion
            {...emails}
            minimum={this.props.minimumEmails}
            defaultState={this.props.defaultState}
            onUpdate={this.updateEmails}
            onError={this.handleError}
            required={this.props.required}
            customDetails={this.firstItemRequired}
            errors={accordionErrors}
            summary={this.emailSummary}
            description={i18n.t('identification.pvq.contacts.collection.emails.summary.title')}
            appendLabel={i18n.t('identification.pvq.contacts.collection.emails.append')}
            scrollIntoView={this.props.scrollIntoView}
            ignoreInitialScrollTo
          >
            <AccordionItem
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
              name="Item"
            >
              <PVQContactInfoItemEmail
                scrollIntoView={this.props.scrollIntoView}
                bind
                name="Email"
                required={this.props.required}
              />
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    ) : (
      <div
        className="section-content contact"
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_CONTACTS.key}
      >
        <h1 className="section-header">{i18n.t('identification.destination.contacts')}</h1>

        {this.props.requireIdentificationContactInfo && (
          <Field
            title={i18n.t('identification.contacts.heading.email')}
            titleSize="h3"
            help="identification.contacts.help.email"
            optional
            className="no-margin-bottom"
            scrollIntoView={false}
          >
            {i18n.m('identification.contacts.para.email')}
          </Field>
        )}

        {this.props.requireIdentificationContactInfo && (
          <Field
            title={i18n.t('identification.contacts.heading.emailHome')}
            titleSize="h4"
            optional={!!WorkEmail.value}
            scrollIntoView={this.props.scrollIntoView}
            attribute="HomeEmail"
          >
            <Email
              attribute="HomeEmail"
              name="HomeEmail"
              className="email-home"
              {...HomeEmail}
              onUpdate={this.updateHomeEmail}
              onError={this.props.onError}
              required={isReview && !WorkEmail.value && this.formType !== formTypes.SF852013}
            />
          </Field>
        )}

        {this.props.requireIdentificationContactInfo && (
          <Field
            title={i18n.t('identification.contacts.heading.emailWork')}
            titleSize="h4"
            optional={!!HomeEmail.value}
            scrollIntoView={this.props.scrollIntoView}
            attribute="WorkEmail"
          >
            <Email
              attribute="WorkEmail"
              name="WorkEmail"
              className="email-work"
              {...WorkEmail}
              onUpdate={this.updateWorkEmail}
              onError={this.props.onError}
              required={isReview && !HomeEmail.value && this.formType !== formTypes.SF852013}
            />
          </Field>
        )}

        <Field
          title={i18n.t('identification.contacts.heading.phoneNumber')}
          titleSize="h3"
          optional
          help="identification.contacts.help.phoneNumber"
          className="no-margin-bottom"
          scrollIntoView={false}
        >
          {i18n.m('identification.contacts.para.phoneNumber')}
        </Field>

        <ErrorMessageList errors={this.getSectionErrors()} />

        <div className={`${klass} telephone-collection`}>
          <Accordion
            {...phoneNumbers}
            minimum={phoneMin}
            defaultState={this.props.defaultState}
            onUpdate={this.updatePhoneNumbers}
            onError={this.handleError}
            required={this.props.required && this.formType !== formTypes.SF852013}
            customDetails={this.firstItemRequired}
            errors={accordionErrors}
            summary={this.phoneNumberSummary}
            description={i18n.t('identification.contacts.collection.phoneNumbers.summary.title')}
            appendLabel={i18n.t('identification.contacts.collection.phoneNumbers.append')}
            scrollIntoView={this.props.scrollIntoView}
            ignoreInitialScrollTo
          >
            <AccordionItem
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required && this.formType !== formTypes.SF852013}
              name="Item"
            >
              <ContactInfoItem
                scrollIntoView={this.props.scrollIntoView}
                bind
                name="Telephone"
                required={this.props.required && this.formType !== formTypes.SF852013}
              />
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    );
  }
}

export const PVQContactInfoItemTelephone = function (props) {
  const phoneNumbersAttribute = `${(props.phoneNumbersAttribute || 'PhoneNumbers')}.items[${props.$index}]`;
  const telephoneAttribute = `${phoneNumbersAttribute}.Item.Telephone`;
  return (
    <Field
      optional
      scrollIntoView={props.scrollIntoView}
      attribute={phoneNumbersAttribute}
      className="minimal-margin-bottom"
    >
      <Telephone
        attribute={telephoneAttribute}
        timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
        name="Telephone"
        typeClass="title"
        showNumberType
        showTimeOfDay
        allowNotApplicable={false}
        bind
        required={props.required}
        {...props}
      />
    </Field>
  );
};

export const PVQContactInfoItemEmail = function (props) {
  const emailAttribute = `${(props.phoneNumbersAttribute || 'Emails')}.items[${props.$index}].Item.Email`;
  return (
    <PVQEmail
      attribute={emailAttribute}
      name="Email"
      bind
      required={props.required}
      {...props}
    />
  );
};

ContactInformation.defaultProps = {
  HomeEmail: {},
  WorkEmail: {},
  PhoneNumbers: EmptyListInstance,
  Emails: EmptyListInstance,
  minimumPhoneNumbers: 1,
  minimumEmails: 1,
  shouldFilterEmptyItems: false,
  onUpdate: () => {},
  onError: (value, arr) => arr,
  dispatch: () => {},
  errors: [],
  defaultState: true,
  isReview: false,
};

export default connectSubsection(ContactInformation, sectionConfig);
